'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksEtkisininSuresiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-etkisinin-suresi-ne-kadar-kalicidir';
  const pageTitle = 'Botoks Etkisinin Süresi: Ne Kadar Kalıcıdır?';
  const pageDescription = 'Botoks etkisi ne kadar sürer, ne zaman yenilenmelidir? Botoksun kalıcılığını uzatan ve kısaltan faktörler nelerdir?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoks Etkisinin Süresi', href: '/tr/blog/botoks-etkisinin-suresi-ne-kadar-kalicidir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks etkisinin süresi ve kalıcılık faktörleri"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Botoks Kalıcılığı
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoks Etkisinin Süresi: Ne Kadar Kalıcıdır?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks, estetik tıpta en sık uygulanan ve en güvenli işlemlerden biridir. Ancak hastaların en çok merak ettiği konulardan biri, botoksun etkisinin ne kadar sürdüğü ve etkisini nasıl koruyabilecekleridir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Kalıcılık süresi; uygulama bölgesi, doz, kas gücü ve kişinin yaşam tarzına göre değişir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Botoks Etkisi Ne Zaman Başlar?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks uygulamasından sonra etkiler genellikle 3–5 gün içinde hissedilmeye başlar. Maksimum etki 10–14 gün içinde ortaya çıkar.
              </p>

              <div className="bg-cyan-50 rounded-xl p-6 mb-6">
                <p className="text-cyan-800">
                  Bu süreçte kas gevşemesi başlar, mimik kırışıklıkları azalır ve cilt daha dinlenmiş bir görünüm alır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Botoks Etkisinin Ortalama Süresi
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Bölge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etki Süresi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Alın</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4–6 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Mimik hareketi fazladır, süre kısalabilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Göz çevresi (kaz ayağı)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–4 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">İnce kaslar nedeniyle daha hızlı çözülür</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kaş arası (glabella)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">5–6 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Daha derin kas grubu, etki daha uzun</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Çene ve boyun</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4–5 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Orta düzey kas aktivitesi</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Terleme (hiperhidroz)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6–9 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Sinir uçları etkilenir, daha kalıcıdır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  💡 Ortalama botoks kalıcılığı: 4–6 ay
                </p>
                <p className="text-blue-800 mt-2">
                  Düzenli uygulamalarda kas hafızası zayıfladığı için süre 8–9 aya kadar uzayabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Etki Süresini Uzatan Faktörler
              </h2>
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Düzenli aralıklarla tekrarlama (kas belleği zayıflar)</li>
                  <li>✅ Alkol ve sigaradan uzak durmak</li>
                  <li>✅ Güneş koruyucu kullanmak</li>
                  <li>✅ Aşırı sıcak (hamam, sauna) ortamlarından kaçınmak</li>
                  <li>✅ Spor sonrası aşırı yüz kası kullanmamak</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Etkiyi Kısaltan Faktörler
              </h2>
              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-red-800 space-y-2">
                  <li>⚠️ Aşırı egzersiz (kas aktivitesi artışı)</li>
                  <li>⚠️ Yüksek metabolizma</li>
                  <li>⚠️ Sık mimik kullanımı</li>
                  <li>⚠️ Kalitesiz veya seyreltilmiş botoks ürünleri</li>
                  <li>⚠️ İlk 24 saatte yapılan masaj veya yüzüstü yatış</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Botoksun Tekrar Zamanı
              </h2>
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <p className="text-purple-800 font-medium mb-3">Genel öneri:</p>
                <p className="text-purple-800 mb-3">📅 4–6 ayda bir yenileme seansı.</p>
                <p className="text-purple-800">
                  Zamanında yapılan tekrarlar hem daha doğal sonuç verir hem de uzun kalıcılık sağlar.
                </p>
                <p className="text-purple-800 mt-3 font-medium">
                  💬 Botoks bir alışkanlık değil, düzenli bakımın parçasıdır.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Botoks etkisinin süresi ve kalıcılık faktörleri"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Botoks etkisi neden herkeste farklı sürer?</h3>
                  <p className="text-gray-700">Kas yapısı, doz ve kişisel metabolizma farkları nedeniyle değişir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etki geçince yüz daha kötü görünür mü?</h3>
                  <p className="text-gray-700">Hayır. Kaslar eski haline döner, yüz sarkmaz.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Botoksu sık yaptırmak zararlı mı?</h3>
                  <p className="text-gray-700">Hayır, uygun aralıklarla yapıldığında güvenlidir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Botoksu kalıcı hâle getirmek mümkün mü?</h3>
                  <p className="text-gray-700">Tam anlamıyla kalıcı değil, ancak düzenli uygulama süreyi uzatır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Dermatology (AAD) – "Duration of Botulinum Toxin Effects"</li>
                <li>Harvard Health Publishing (2024) – "How Long Does Botox Last?"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – "Botulinum Toksin Uygulama Rehberi"</li>
                <li>ISAPS Global Report 2024 – "Botulinum Toxin Longevity and Trends"</li>
                <li>Mayo Clinic – "Understanding the Duration of Botox Treatment"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#kalıcılık</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#mimik</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#kırışıklık</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#yüzbakımı</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Botoks Hakkında Bilgi Alın</h3>
              <p className="mb-6">Botoks etkisinin süresi ve kalıcılık faktörleri hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoks etkisinin süresi hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-cyan-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-ne-kadar-sure-gider"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">⏱️ Botoks Ne Kadar Süre Gider?</h4>
              <p className="text-gray-600 text-sm">Botoksun etkisi ne kadar sürer? Ortalama kalıcılık süresi ve etkisini uzatmanın yolları.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir ve Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama sürecinin ayrıntılı anlatımı.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
